// This file is based on `SupplierAutosuggestFilter`, but with key differences:
// - It only handles `exact` and `group` supplier selections.
// - Users cannot deselect a list of suppliers by clicking on their supplier group name.
// - Includes form submission logic with AJAX and error handling.
// The refactoring extracts shared logic while simplifying the competitor selection process.

import React, { Component } from "react";
import $ from 'jquery';
import Rails from '@rails/ujs';
import update from "immutability-helper";

import SupplierGroupStateManager from "../supplier_group_state_manager";

import AutosuggestWrapper from "../filters/autosuggest/autosuggest_wrapper";
import Button from "../ui/button";
import BackIcon from "../../assets/images/icons/arrow-left.svg";
import Link from "../ui/link";
import SupplierGroupIcon from "../../assets/images/icons/group.svg";
import SelectedCheckbox from "../ui/selected_checkbox";
import SupplierGroup from "../ui/supplier_group";

export default class CompetitorsListAutosuggest extends Component {
  constructor(props) {
    super(props);
    this.state = {
      suppliers: {
        exact: [],
        group: {}
      },
      alertMessage: null,
      loading: false
    };
  }

  // Initialize the group manager to track the selected supplier groups.
  componentDidMount() {
    this.groupManager = new SupplierGroupStateManager(
      this.state.suppliers.group,
      (updateObj) => this.handleGroupInputChange(updateObj)
    );
  }

  // Updates the state when a supplier group is selected/modified. 
  handleGroupInputChange = (updateObj) => {
    this.setState((prevState) =>
      update(prevState, {
         suppliers: updateObj
      })
    );
  };

  // Removes the suppliers from the selected list.
  handleDeselect = (filter, index) => {
    this.setState((prevState) =>
      update(prevState, {
        suppliers: { [filter]: { $splice: [[index, 1]] } }
      })
    );
  };

  // Adds a supplier or a supplier_groups' suppliers to the selected list.
  handleFieldChange = (suggestion) => {
    if (suggestion.suggestionType === "group" || suggestion.groupName) {
      return this.groupManager.handleChange("select", suggestion);
    }
    this.setState((prevState) =>
      update(prevState, { suppliers: { exact: { $push: [suggestion] } } })
    );
  };

  renderLocation(option) {
    const location = [option.city, option.country]
      .filter((a) => { return a !== null; })
      .join(', ');

    return <div className="text-grey-700 text-sm">
      {location}
    </div>;
  }

  // Prevent already selected suppliers from appearing in the supplier auto suggestions.
  shouldRenderSuggestion = (suggestion) => {
    if (typeof suggestion === "string") return true;
    const { exact } = this.state.suppliers;
    const buildSuggestionId = (supplier) => `supplier-${supplier.id}`;
    const suggestionsToIgnore = [
      ...exact.map(buildSuggestionId),
      ...this.groupManager.resultsToIgnore(),
    ];
    return !suggestionsToIgnore.includes(suggestion.suggestionId);
  };

  // Display the selected suppliers.
  renderSuggestion = (option, additionalClasses = "") => {
    return (
      <div className={`flex space-x-2 cursor-pointer text-grey-900 ${additionalClasses}`}>
        {option.suggestionType === "group" && (
          <SupplierGroupIcon width={21} height={21} className="supplier-icon min-w-[21px] min-h-[21px] cursor-auto" />
        )}
        <div className="flex flex-col gap-y-1">
          <div className="name bold">{option.name}</div>
          {option.suggestionType === "supplier" && this.renderLocation(option)}
        </div>
      </div>
    );
  };

  handleSubmit = () => {
    const { suppliers } = this.state;
    this.setState({ loading: true })

    const exactSupplierIds = suppliers.exact.map((supplier) => supplier.id);
    const groupSupplierIds = Object.values(suppliers.group).flatMap(group => {
      if (group.selected) {
        // If the whole group has been selected, return the ids from the suppliersArray
        return group.suppliers.map(supplier => supplier.id)
      } else {
        // Otherwise, return the selected suppliers.
        return group.selectedSuppliers.map(supplier => supplier.id)
      }
    });
    const supplierIds = [...exactSupplierIds, ...groupSupplierIds];

     this.request = $.ajax({
       type: "POST",
       url: "/saved_competitors/bulk_create",
       data: {
         authenticity_token: Rails.csrfToken() || "",
         supplier_ids: supplierIds,
         origin: "Competitors page",
       },
       success: () => {
         window.location.reload();
       },
       error: () => {
         this.setState({
           loading: false,
           alertMessage: "Whoops! Something went wrong. Please try again."
         })
       },
     });
  }

  render = () => {
    const { suppliers, loading, alertMessage } = this.state;
    const handleGroupChange = this.groupManager?.handleChange.bind(this.groupManager);
    const groups = this.groupManager?.getGroups() || [];
    const hasSelectedSuppliers = suppliers.exact.length > 0 || Object.keys(suppliers.group).length > 0;
    // If the user navigated from a page within the Tussell website, allow them to go back normally. 
    // Otherwise, redirect them to the suppliers page.
    const backUrl = document.referrer.includes(window.location.origin) ? document.referrer : "/suppliers";

    if (alertMessage) alert(alertMessage)

    return (
      <div className="space-y-4">
        <h4 className="text-md">Build your competitors list below to get started</h4>
        <AutosuggestWrapper
          placeholder="Search competitor by supplier name"
          name="supplier_list_suppliers"
          onChange={this.handleFieldChange}
          url="supplier_suggestions.json"
          suggestionValue={(option) => option.name || option}
          itemsProps="py-1"
          renderSuggestion={(suggestion) => this.renderSuggestion(suggestion, "py-1 px-2 items-center hover:bg-grey-200")}
          shouldRenderSuggestion={this.shouldRenderSuggestion}
        />
       { hasSelectedSuppliers && 
          <div id="selected-suppliers" className="max-h-64 overflow-y-auto flex flex-col space-y-1">
            <div className="text-grey-900">Supplier name</div>
            {suppliers.exact.map((supplier, i) => (
              <div key={`supplier-${i}`} className="space-y-1">
                <SelectedCheckbox
                  key={supplier.id}
                  onDeselect={() => this.handleDeselect("exact", i)}
                  label={supplier.name}
                  labelClasses="text-grey-900"
                />
                {this.renderLocation(supplier)}
              </ div>
            ))}
            {groups.map((group) => (
              <SupplierGroup
                key={`supplier-group-${group.id}`}
                group={group}
                supplierName={(supplier) => (
                  <div className="text-grey-900">
                    {supplier.name}
                    {this.renderLocation(supplier)}
                  </div>
                )}
                handleChange={handleGroupChange}
                label={this.renderSuggestion(group)}
                displayCheckbox={false}
              />
            ))}
          </div>
        }
        <div data="text-cta" className="flex flex-row justify-between">
          <Button
            type="primary"
            disabled={!hasSelectedSuppliers || loading}
            onClick={(e) => this.handleSubmit(e)}
          >
            Confirm
          </Button>
          <Link
            href={backUrl}
            type="tertiary"
          >
            <BackIcon class="w-[9px]"/>
            Back
          </Link>
        </div>
      </div>
    );
  };
}
